import { useRequest } from 'features/request/useRequest';
import { ActKc2StatusEnum } from 'gql/graphql';
import { replacerCost } from 'utils/replacerCost';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerSetActKC2AcceptedCostGql, uerSetActKC2PlanCostGql } from './workOrderPriceKC2CostEditQueries';

export const useRequestsKC2CostEdit = (
    actKC2Id: number,
    workOrderPriceId: number,
    defaultValue: string,
    actKC2Status: ActKc2StatusEnum,
) => {
    const dispatch = useDispatch();

    const isKC2StatusInProgress = actKC2Status === ActKc2StatusEnum.InProgress;
    const isKC2StatusInOnCheck = actKC2Status === ActKc2StatusEnum.OnCheck;

    const [actKC2PlanCostData, actKC2PlanCostStatus, actKC2PlanCostError, actKC2PlanCostRequest] = useRequest({
        requestId: 'actKC2PlanCost',
        query: uerSetActKC2PlanCostGql,
        withEffect: false,
    });

    const [actKC2AcceptedCostData, actKC2AcceptedCostStatus, actKC2AcceptedCostError, actKC2AcceptedCostRequest] =
        useRequest({
            requestId: 'actKC2AcceptedCost',
            query: uerSetActKC2AcceptedCostGql,
            withEffect: false,
        });

    const handleOnChange = useCallback(
        (value: string) => {
            if (isKC2StatusInProgress) {
                dispatch(
                    actKC2PlanCostRequest({
                        actKC2Id,
                        workOrderPriceId,
                        cost: replacerCost(value),
                    }),
                );
            }

            if (isKC2StatusInOnCheck) {
                dispatch(
                    actKC2AcceptedCostRequest({
                        actKC2Id,
                        workOrderPriceId,
                        cost: replacerCost(value),
                    }),
                );
            }
        },
        [
            actKC2AcceptedCostRequest,
            actKC2Id,
            actKC2PlanCostRequest,
            dispatch,
            isKC2StatusInOnCheck,
            isKC2StatusInProgress,
            workOrderPriceId,
        ],
    );

    const getRequestData = useMemo(() => {
        if (isKC2StatusInProgress) {
            return actKC2PlanCostData?.uerSetActKC2PlanCost;
        }

        return actKC2AcceptedCostData?.uerSetActKC2AcceptedCost;
    }, [actKC2AcceptedCostData, actKC2PlanCostData, isKC2StatusInProgress]);

    const getRequestError = useMemo(() => {
        if (isKC2StatusInProgress) {
            return actKC2PlanCostError;
        }

        return actKC2AcceptedCostError;
    }, [isKC2StatusInProgress, actKC2PlanCostError, actKC2AcceptedCostError]);

    const getRequestStatus = useMemo(() => {
        if (isKC2StatusInProgress) {
            return actKC2PlanCostStatus;
        }

        return actKC2AcceptedCostStatus;
    }, [isKC2StatusInProgress, actKC2PlanCostStatus, actKC2AcceptedCostStatus]);

    return { handleOnChange, getRequestData, getRequestError, getRequestStatus };
};
